import { useCallback, useEffect, useRef, useState } from 'react';
import ModalVoltar from '../../components/ModalVoltar';
import { aoErroImagem, urlOtimizada } from '../../lib/imagem';
import './SlideshowImovel.css';

// Slideshow das fotos do imóvel (janela de detalhe da lista).
// - foto grande com fundo desfocado (nenhuma foto fica com "tarjas" pretas), setas, contador;
// - tiras de miniaturas clicáveis (usam as miniaturas 120x120, leves) com a atual em destaque;
// - arrastar o dedo (celular) ou teclas ← → trocam de foto; ▶ liga a apresentação automática;
// - tocar/clicar na foto abre em tela cheia (com anterior/próxima também).
const INTERVALO_MS = 3500;

export default function SlideshowImovel({ fotos }) {
  const [atual, setAtual] = useState(0);
  const [tocando, setTocando] = useState(false);
  const [ampliada, setAmpliada] = useState(false);
  const inicioToque = useRef(null);
  const tiraRef = useRef(null);
  const total = fotos.length;

  const ir = useCallback((indice) => setAtual(((indice % total) + total) % total), [total]);
  const proxima = useCallback(() => ir(atual + 1), [ir, atual]);
  const anterior = useCallback(() => ir(atual - 1), [ir, atual]);

  // PRÉ-CARREGA todas as fotos (no tamanho do slideshow) assim que a janela abre, 20 por vez, pra
  // trocar de foto sem esperar o download (sem "quadro em branco"). A atual já carrega pelo <img>.
  useEffect(() => {
    let cancelado = false;
    const carregando = [];
    let proxima = 0;
    function carregarProxima() {
      if (cancelado || proxima >= fotos.length) return;
      const img = new Image();
      carregando.push(img);
      img.onload = carregarProxima;
      img.onerror = carregarProxima;
      img.src = urlOtimizada(fotos[proxima++].url, 1280);
    }
    for (let k = 0; k < 20; k++) carregarProxima();
    return () => {
      cancelado = true;
      carregando.forEach((img) => {
        img.onload = null;
        img.onerror = null;
      });
    };
  }, [fotos]);

  // apresentação automática (para quando a tela cheia está aberta ou só há uma foto)
  useEffect(() => {
    if (!tocando || total < 2 || ampliada) return undefined;
    const t = setInterval(() => setAtual((i) => (i + 1) % total), INTERVALO_MS);
    return () => clearInterval(t);
  }, [tocando, total, ampliada]);

  // teclado
  useEffect(() => {
    function aoTeclar(e) {
      if (e.key === 'ArrowRight') proxima();
      else if (e.key === 'ArrowLeft') anterior();
    }
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [proxima, anterior]);

  // mantém a miniatura atual visível na tira
  useEffect(() => {
    const el = tiraRef.current?.children[atual];
    if (el?.scrollIntoView) el.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [atual]);

  function aoTocarInicio(e) {
    inicioToque.current = e.touches[0].clientX;
  }
  function aoTocarFim(e) {
    if (inicioToque.current === null) return;
    const delta = e.changedTouches[0].clientX - inicioToque.current;
    inicioToque.current = null;
    if (Math.abs(delta) > 40) (delta < 0 ? proxima : anterior)();
  }

  if (total === 0) return null;
  const foto = fotos[atual];

  return (
    <div className="slideshow">
      <div className="slideshow-palco" onTouchStart={aoTocarInicio} onTouchEnd={aoTocarFim}>
        <div className="slideshow-fundo" style={{ backgroundImage: `url(${urlOtimizada(foto.url, 240, { quadrada: true })})` }} />
        <img
          key={foto.id}
          className="slideshow-foto"
          src={urlOtimizada(foto.url, 1280)}
          onError={aoErroImagem(foto.url)}
          alt={`Foto ${atual + 1} de ${total}`}
          onClick={() => setAmpliada(true)}
        />

        <span className="slideshow-contador">
          {atual + 1} / {total}
        </span>
        {total > 1 && (
          <>
            <button type="button" className="slideshow-seta esquerda" onClick={anterior} aria-label="Foto anterior">
              ‹
            </button>
            <button type="button" className="slideshow-seta direita" onClick={proxima} aria-label="Próxima foto">
              ›
            </button>
            <button type="button" className="slideshow-play" onClick={() => setTocando((t) => !t)} aria-label={tocando ? 'Pausar apresentação' : 'Iniciar apresentação'}>
              {tocando ? '❚❚' : '▶'}
            </button>
          </>
        )}
        <button type="button" className="slideshow-ampliar" onClick={() => setAmpliada(true)} aria-label="Ampliar foto">
          ⤢
        </button>
      </div>

      {total > 1 && (
        <div className="slideshow-tira" ref={tiraRef}>
          {fotos.map((f, i) => (
            <button key={f.id} type="button" className={`slideshow-mini${i === atual ? ' ativa' : ''}`} onClick={() => setAtual(i)} aria-label={`Ir para a foto ${i + 1}`}>
              <img src={urlOtimizada(f.url, 240, { quadrada: true })} onError={aoErroImagem(f.url)} alt="" loading="lazy" />
            </button>
          ))}
        </div>
      )}

      <ModalVoltar show={ampliada} onHide={() => setAmpliada(false)} fullscreen dialogClassName="slideshow-tela-cheia">
        <ModalVoltar.Header closeButton closeVariant="white" className="bg-dark text-white border-0">
          <ModalVoltar.Title className="h6">
            {atual + 1} / {total}
          </ModalVoltar.Title>
        </ModalVoltar.Header>
        <ModalVoltar.Body className="bg-dark p-0 d-flex align-items-center justify-content-center position-relative" onTouchStart={aoTocarInicio} onTouchEnd={aoTocarFim}>
          <img src={urlOtimizada(foto.url, 1920)} onError={aoErroImagem(foto.url)} alt={`Foto ${atual + 1}`} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          {total > 1 && (
            <>
              <button type="button" className="slideshow-seta esquerda" onClick={anterior} aria-label="Foto anterior">
                ‹
              </button>
              <button type="button" className="slideshow-seta direita" onClick={proxima} aria-label="Próxima foto">
                ›
              </button>
            </>
          )}
        </ModalVoltar.Body>
      </ModalVoltar>
    </div>
  );
}
